import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import {
  AudioPresets,
  type ScreenShareCaptureOptions,
  Track,
  type TrackPublishOptions,
} from 'livekit-client'

import { isTeacher } from './identity.ts'

/**
 * Screen-share policy: who CAN share, which share the room looks at, and what
 * to tell a child when their share fails. Pure functions — no room access.
 *
 * The class shows exactly ONE share at a time. That is product policy, not a
 * LiveKit feature: the SFU will happily carry a share from every participant,
 * so we police it in the UI, pick deterministically when two race, and let the
 * teacher force-stop the loser through the existing moderation path.
 */

/**
 * The options EVERY share must publish with — one constant so a future call
 * site cannot forget them (the first cohort's blurry shares came from exactly
 * that: publish defaults tuned for cameras silently applying to the share).
 *
 * contentHint 'text': our shares are code and worksheets; Chrome's encoder
 * biases toward sharp glyphs over smooth motion, which is the trade a coding
 * lesson wants. NO `resolution` here, deliberately — livekit-client picks the
 * right capture default per browser, and passing any resolution on Safari 17
 * triggers a low-resolution capture bug (see ScreenShareCaptureOptions docs).
 *
 * `audio` is what lets the class HEAR a shared video. Without it
 * getDisplayMedia is called with `audio: false`, no ScreenShareAudio track is
 * ever created, and a teacher playing a lesson video presents it in silence —
 * which is how every share worked until now. The receiving side was already
 * built for it (Stage's RemoteAudio) and so was the server (the student token
 * grants `screen_share_audio`, and stop-share unpublishes it as a companion);
 * only the request was missing.
 *
 * The three processing flags are OFF and that is the load-bearing part. Chrome
 * applies its microphone processing chain to display audio too, and every one
 * of them is designed for a voice in a room: automatic gain control audibly
 * pumps on mastered music, noise suppression eats soft beds and sound effects,
 * and echo cancellation has nothing to cancel on a tab's own output. They are
 * bare booleans (an "ideal" constraint in WebRTC, never "exact"), so a browser
 * that ignores them should degrade to its defaults rather than reject the call.
 *
 * "Should" is doing real work in that sentence. A REJECTED getDisplayMedia takes
 * the video with it, and a share that fails is a lesson that moves to another calling app — so
 * if a first cohort ever reports the share button failing outright on a machine
 * where it used to work, THIS is the first thing to suspect, and the fix is to
 * fall back to a plain `audio: true` for the session. Not built pre-emptively:
 * every production WebRTC app disables these three the same way, and an untested
 * retry path is its own risk.
 *
 * Deliberately NOT set:
 *
 * - `channelCount` — mono is right for a classroom: a stereo image nobody hears
 *   on a phone speaker, paid for in bitrate we need for the teacher's voice.
 *
 *   Chrome usually hands us a stereo tab-audio track whether we ask or not, so
 *   the CAPTURE may well be stereo while the PUBLISHED stream is mono. What
 *   actually keeps it mono, re-read against livekit-client 2.21.0, is the SDK's
 *   own `forceStereo: false` publish default: the check is
 *   `forceStereo ?? isStereoInput`, and because that default is `false` rather
 *   than undefined it SHORT-CIRCUITS the `channelCount === 2` sniff entirely.
 *   The sniff is not our guard rail, and reasoning about it as one is how a
 *   future change to `forceStereo` would surprise someone.
 *
 *   The other half of the old warning here — that stereo silently costs us
 *   Opus RED — is no longer true either. The SDK only forces `red`/`dtx` off
 *   for a stereo track `if (opts.red === undefined)`, and room-options.ts sets
 *   both to `true` explicitly, precisely so a default change cannot take them.
 *   Packet-loss resilience on Egyptian mobile survives regardless.
 * - `suppressLocalAudioPlayback` — true would mute the shared tab on the
 *   teacher's own machine. A teacher talking over a video has to hear the video.
 */
export const SCREEN_SHARE_CAPTURE: ScreenShareCaptureOptions = {
  contentHint: 'text',
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  },
  /**
   * Tab (or window) audio only — never the machine's output mix.
   *
   * "Share system audio" captures whatever the speakers are playing, and on the
   * teacher's machine that INCLUDES the class: our own <audio> elements playing
   * the students back. Capturing that and publishing it returns every child's
   * voice to the room a beat late, which is a feedback loop rather than a
   * feature. Excluding it costs us audio from desktop applications (a local
   * video player, a slide deck with sound) on Windows — macOS Chrome cannot
   * capture system audio at all, so half the fleet never had it — and our
   * content is web content, which tab audio carries.
   *
   * Chrome 105+; older builds ignore the hint and still offer the checkbox.
   */
  systemAudio: 'exclude',
  /**
   * The lesson tab itself is not offerable. This was always pointless as VIDEO
   * (a teacher sharing the classroom into the classroom is the hall-of-mirrors
   * bug), and with audio requested it becomes the same feedback loop as system
   * audio by a shorter route: share this tab, tick "also share tab audio", and
   * the room's own output is republished into the room.
   *
   * Narrow, and worth being honest about: this removes the lesson tab from the
   * picker's list of TABS. It does not hide the classroom window from a teacher who
   * picks "Entire screen" — the hall of mirrors is still reachable that way, it
   * just cannot carry audio any more, because system audio is excluded above.
   *
   * Chrome 107+; ignored below that.
   */
  selfBrowserSurface: 'exclude',
}

/**
 * How the share is published — video AND audio.
 *
 * livekit-client hands this one object to both tracks of the share
 * (`createScreenTracks` returns them together and publishes each with the
 * options passed to `setScreenShareEnabled`), and each track ignores the
 * other's fields. So this is the only place that can correct the room-wide
 * publish defaults for share audio, and it has to: those defaults are tuned
 * for a child's voice on a congested phone, and applying them to a film
 * soundtrack is audibly wrong.
 *
 * - `degradationPreference` — under congestion the encoder drops FRAMES and
 *   keeps the text readable. The room default is 'maintain-framerate' (right
 *   for faces: lip sync over sharpness), and livekit-client applies its
 *   per-source screen-share default ONLY when no preference is set at all, so
 *   the share must carry its own.
 * - `audioPreset` — 48 kbps mono instead of the room's 24 kbps speech preset.
 *   Opus at 48 kbps carries music and film dialogue perfectly adequately for a
 *   lesson; 24 kbps is a telephone, and the 96 kbps preset would spend a
 *   student's downlink on fidelity nobody asked for. The share video already
 *   has a 2.5 Mbps ceiling on the same uplink.
 * - `dtx` — OFF, and this is not optional. Discontinuous transmission stops
 *   sending during silence, which is exactly right for a talking head and
 *   exactly wrong for content: quiet intros vanish, the first syllable after
 *   every pause is clipped, and soft passages come back with comfort noise
 *   instead of music. It reads as broken streaming, not as compression.
 * - `red` — ON. Opus redundancy roughly doubles this track's payload (~48 →
 *   ~96 kbps) to survive packet loss, which on an Egyptian mobile path is the
 *   trade worth making, and is still noise beside the video. If a teacher's
 *   uplink is ever the constraint, drop RED before dropping the bitrate.
 *
 * Both are named explicitly rather than left to inherit: `dtx` and `red` are
 * `true` in room-options for the microphone, so silence here would mean
 * inheriting them.
 */
export const SCREEN_SHARE_PUBLISH: TrackPublishOptions = {
  degradationPreference: 'maintain-resolution',
  audioPreset: AudioPresets.music,
  dtx: false,
  red: true,
}

/**
 * How loud a share's audio plays relative to everyone's voices.
 *
 * A YouTube video is mastered close to full scale and the microphone is pushed
 * there too by automatic gain control, so at parity a video buries the teacher.
 *
 * 0.7 is about -3 dB, which is deliberately modest, and the reason is which
 * error is recoverable. Attenuate too little and a teacher hears themselves being
 * buried and turns the tab down — the system corrects. Attenuate too much and
 * the class cannot hear the video while the teacher, who receives none of this and
 * hears their own tab at full volume, has no way to notice; they turn the SOURCE
 * up instead, which runs the content hot and defeats the attenuation for
 * everyone. Starting quiet teaches exactly the wrong habit.
 *
 * This is the one number in the feature that has to be set by ear rather than by
 * argument. Change it after listening to real content on a real Android handset,
 * not before.
 *
 * Applied on the RECEIVING side, per track, so it only touches share audio and
 * never a microphone. And note the archive is not attenuated: the recording is
 * composed by LiveKit's own egress template, so a QA reviewer hears the share
 * slightly louder relative to the teacher than the class did.
 */
export const SHARE_AUDIO_VOLUME = 0.7

/**
 * Can this device actually share a screen?
 *
 * The presence of `getDisplayMedia` is necessary but NOT sufficient: Chrome and
 * Firefox on Android DEFINE the method and then reject every call with
 * NotAllowedError — there is no capture UI on the platform at all. iOS Safari
 * is the same. Since most of our students are on Android phones, trusting the
 * API check alone would let a teacher ask a child to do something their phone can
 * never do, and the child would just see a failure they cannot fix.
 *
 * There is no silent probe: getDisplayMedia needs a user gesture, so we cannot
 * test-call it at join. Platform exclusion is the honest signal available.
 */
export function detectCanScreenShare(): boolean {
  if (typeof navigator === 'undefined') return false
  if (typeof navigator.mediaDevices?.getDisplayMedia !== 'function') return false
  const ua = navigator.userAgent || ''
  // Android (phones AND tablets — neither can capture) and iOS/iPadOS.
  if (/Android/i.test(ua)) return false
  if (/iPhone|iPad|iPod/i.test(ua)) return false
  // iPadOS 13+ reports as Macintosh with touch points; it also cannot capture.
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return false
  return true
}

/**
 * Is this reference a share that is actually PRESENTING?
 *
 * The `isMuted` half is the whole point, and it exists because of a real bug: a
 * teacher pressing "Stop their screen share" left a black rectangle on the stage
 * with "… is presenting" still under it.
 *
 * LiveKit has no server API to unpublish someone else's track — the only lever
 * moderation has is `MutePublishedTrack`. livekit-client answers that by calling
 * `pub.mute()` with NO special case for a share, even though its own local path
 * knows better ("screenshare cannot be muted, unpublish instead") and
 * unpublishes. So a stopped share stays PUBLISHED and muted: the publication is
 * still there, it still has a track, and it delivers nothing.
 *
 * Every place that asked "is anyone sharing?" by testing the source alone
 * therefore answered yes forever — which is how one stop button produced a black
 * stage, a stuck "is presenting" label, a stop item that did nothing on a second
 * press, and an "ask to share" control that could never be offered again.
 *
 * A muted share is not a share, so ask that question here, once, and let every
 * caller share the answer. The camera tile has always filtered `isMuted` this
 * way; the share surface simply never did.
 *
 * Note this deliberately does NOT require a subscribed `track`: a publication
 * that exists without one yet is the pre-subscribe window, which the stage
 * renders as "connecting" rather than hiding.
 */
export function isLiveScreenShare(ref: TrackReferenceOrPlaceholder): boolean {
  if (ref.source !== Track.Source.ScreenShare) return false
  if (!ref.publication) return false
  return !ref.publication.isMuted
}

/**
 * Which share the whole room renders and annotates.
 *
 * Deterministic on purpose. `tracks.find(...)` returned whichever track the SDK
 * happened to order first, so during a two-share race different participants
 * could be looking at different screens while the ink layer bound to a third
 * opinion. Order: my own share (I must see what I am presenting) → the
 * teacher's (the lesson outranks a student demo) → the lowest track sid, which
 * every client sorts the same way.
 */
export function pickScreenShare(
  tracks: TrackReferenceOrPlaceholder[],
): TrackReferenceOrPlaceholder | undefined {
  const shares = tracks.filter(isLiveScreenShare)
  if (shares.length <= 1) return shares[0]
  const local = shares.find((track) => track.participant.isLocal)
  if (local) return local
  const teacher = shares.find((track) => isTeacher(track.participant.identity))
  if (teacher) return teacher
  return [...shares].sort((a, b) =>
    (a.publication?.trackSid ?? a.participant.identity).localeCompare(
      b.publication?.trackSid ?? b.participant.identity,
    ),
  )[0]
}

/** Why a share attempt ended, in terms a child can act on. */
export type ShareFailure =
  | 'cancelled'
  | 'unfocused'
  | 'unsupported'
  | 'blocked'
  | 'failed'
  | 'systemDenied'

/**
 * The copy for a failed share. Lives here rather than in a component because
 * BOTH share paths need it and only one of them used to have it: the student's
 * ask-to-share prompt explained failures, while the teacher's own toolbar button
 * swallowed them in a bare `catch`.
 *
 * That asymmetry hurt most on Windows, where `unfocused` (InvalidStateError) is
 * routine rather than exotic — a teacher on a second monitor with another
 * application in the foreground hits it constantly, and the only feedback was a
 * button that flickered and did nothing. "The app is broken" is a reasonable
 * conclusion from that, and reaching for another calling app is a reasonable next step.
 */
export function shareFailureKey(
  failure: ShareFailure,
):
  | 'share.errUnfocused'
  | 'share.errUnsupported'
  | 'share.errBlocked'
  | 'share.errSystemDenied'
  | 'share.errFailed' {
  switch (failure) {
    case 'unfocused':
      return 'share.errUnfocused'
    case 'unsupported':
      return 'share.errUnsupported'
    case 'blocked':
      return 'share.errBlocked'
    case 'systemDenied':
      return 'share.errSystemDenied'
    default:
      return 'share.errFailed'
  }
}

/**
 * Map a getDisplayMedia rejection onto something worth showing an 8-year-old.
 * Cancelling the picker is NOT an error — it is a decision, and it must not be
 * dressed up as a failure.
 */
export function classifyShareError(error: unknown): ShareFailure {
  const name = (error as { name?: string })?.name || ''
  const message = String((error as { message?: string })?.message || '')
  if (name === 'NotAllowedError') {
    if (/denied by system/i.test(message)) return 'systemDenied'
    if (/permissions? policy|disallowed by permissions|feature policy/i.test(message))
      return 'blocked'
    // Treated as CANCELLED, not blocked: Chrome's plain picker-cancel rejects
    // with "Permission denied", so sniffing the message for permission words
    // turned the commonest, calmest outcome into a scary failure. A genuine
    // permissions-policy block gets the same soft dismissal — a child cannot
    // fix either one from inside the prompt, and calm beats precise here.
    return 'cancelled'
  }
  if (name === 'InvalidStateError') return 'unfocused'
  if (name === 'NotSupportedError' || /unsupported|not supported/i.test(message))
    return 'unsupported'
  if (name === 'NotReadableError' || name === 'NotFoundError') return 'blocked'
  return 'failed'
}
